import React, { useEffect, useState } from 'react';
import { Translation, getI18n } from 'react-i18next';
import { Typography, Form, Button, Collapse, Tooltip } from 'antd';
import { withRouter } from 'react-router';
import { PlusOutlined } from '@ant-design/icons';
import Logger from '../../../../../lib/Logger';
import notification from '../../../elements/lib/NotificationWrapper';
import LearningAdminModuleSerieFormFooter from './LearningAdminModuleSerieFormFooter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModuleInputRow from './ModuleInputRow';
import PublicStatusInput from './PublicStatusInput';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { pathTo } from '../../../Routes';
import QueryString from '../../../../../lib/QueryString';
import {
  MixpanelTracker,
  mixpanelEventsEnum,
} from '../../../../../lib/Mixpanel';
const { Title, Text } = Typography;
const { Panel } = Collapse;

const layout = {
  main: {
    wrapperCol: { span: 24 },
  },
};

const reFileKey = /(?:(.*?)\/)?(.*?)\.(\w+)/;

const LearningAdminModuleSerieFormStep2 = ({
  moduleSerieId,
  load,
  partnershipId,
  data,
  isSubmitting,
  errors,
  destroyForm,
  isLoading,
  setConfirmationModalOpen,
  isChangeValue,
  modules,
  step,
  setIsChangeValue,
  oldParams,
  setTargetPath,
  nextStep,
  history,
  view,
  activeKey,
  prevStep,
  setPendingAction,
  screen,
  getSignedUrl,
  removes,
  trackerActions,
  partnershipName,
  ...props
}) => {
  const [form] = Form.useForm();
  const [activeKeys, setActiveKeys] = useState([]);
  const [defaultPublicStatus, setDefaultPublicStatus] = useState(2);
  const [deleteImageIds, setDeleteImageIds] = useState([]);
  const [deleteModuleIds, setDeleteModuleIds] = useState([]);

  // load record data from API
  useEffect(() => {
    if (moduleSerieId && screen === 'edit') {
      load(partnershipId, moduleSerieId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnershipId, moduleSerieId, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);
  useEffect(() => {
    if (moduleSerieId && screen === 'edit' && !isSubmitting) {
      const dataObj = JSON.parse(dataString);
      form.setFieldsValue(dataObj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dataString, isSubmitting, moduleSerieId]);

  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    return () => {
      destroyForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnershipId, destroyForm]);

  const uploadMedia = (file, path) => {
    return new Promise((resolve, reject) => {
      props.getUploadUrl(
        file.name.split('.').pop(),
        path,
        (success, upload_url, file_id) => {
          if (!success) return resolve(null);

          props.upload(upload_url, file.originFileObj, (uploadSuccess) => {
            if (!uploadSuccess) return resolve(null);

            const directory = `${path}/`;
            file_id = file_id.replace(directory, '');
            const matches = reFileKey.exec(file_id);

            const updateData = {
              file_directory: directory.slice(0, -1),
              file_name: matches[2],
              file_extension: matches[3],
            };

            props.createImagePartnership(
              partnershipId,
              updateData,
              (createSuccess, imageId) => {
                if (createSuccess) {
                  resolve(imageId);
                } else {
                  resolve(null);
                }
              }
            );
          });
        }
      );
    });
  };

  const successAction = () => {
    const page = 'LearningsAdminScreen';
    notification.success(getI18n().t('feedback_form_success'));
    setIsChangeValue(false);
    destroyForm();
    const path = QueryString.append(pathTo(page), oldParams);
    history.push(path);

    MixpanelTracker.mixpanelTrack(
      mixpanelEventsEnum.PAGES.ACCESS_PAGE_ADMINISTRATOR,
      {
        partnershipName: partnershipName,
        partnershipId: partnershipId,
        page: page,
      }
    );

    if (deleteModuleIds.length > 0) {
      removes(partnershipId, { module_ids: deleteModuleIds });
    }
    if (deleteImageIds.length > 0) {
      deleteImageIds.forEach((id) => {
        props.deleteImage(partnershipId, id);
      });
    }
  };

  const submitData = async (values) => {
    Logger.log('debug', `LearningAdminModuleSerieFormStep2.submitData(###)`);
    let payload = [];
    const imageUploadErrors = [];

    for (const input of Object.keys(data)) {
      if (input in values) {
        if (input === 'modules') {
          if (Array.isArray(values[input])) {
            payload = await Promise.all(
              values[input].map(async (x, i) => {
                if (x !== null && x !== undefined) {
                  let {
                    categories,
                    cover_image,
                    cover_image_id,
                    media,
                    media_id,
                    public_status,
                    video_vimeo_url,
                    video_youtube_url,
                    upload_content,
                    ...module
                  } = x;
                  if (cover_image_id === undefined || cover_image_id === null) {
                    cover_image_id = await uploadMedia(
                      cover_image[0],
                      'learning-module-cover-images'
                    );
                  }

                  if (cover_image_id !== undefined && cover_image_id !== null) {
                    form.setFields([
                      {
                        name: ['modules', i, 'cover_image_id'],
                        value: cover_image_id,
                      },
                    ]);
                  } else {
                    form.setFields([
                      {
                        name: ['modules', i, 'cover_image'],
                        errors: [
                          'Error uploading image. Check if the image is valid and try again.',
                        ],
                      },
                    ]);
                    imageUploadErrors.push(i);
                  }

                  if (public_status === undefined || public_status === null) {
                    public_status = defaultPublicStatus;
                  }

                  if (upload_content === 1) {
                    video_vimeo_url = null;
                    video_youtube_url = null;

                    if (
                      (media_id === undefined || media_id === null) &&
                      media
                    ) {
                      media_id = await uploadMedia(
                        media[0],
                        'learning-module-media'
                      );
                    }

                    if (media_id !== undefined && media_id !== null) {
                      form.setFields([
                        {
                          name: ['modules', i, 'media_id'],
                          value: media_id,
                        },
                      ]);
                    } else {
                      form.setFields([
                        {
                          name: ['modules', i, 'media'],
                          errors: [
                            'Error uploading image. Check if the image is valid and try again.',
                          ],
                        },
                      ]);
                      imageUploadErrors.push(i);
                    }
                  } else if (upload_content === 2) {
                    video_vimeo_url = null;
                    media_id = null;
                  } else if (upload_content === 3) {
                    video_youtube_url = null;
                    media_id = null;
                  }

                  module = {
                    ...module,
                    categories_ids: categories ?? [],
                    public_status: public_status,
                    cover_image_id,
                    video_vimeo_url,
                    media_id,
                    video_youtube_url,
                    upload_content,
                  };

                  delete module.cover_image;
                  delete module.media;
                  delete module.categories;
                  delete module.modules_module_categories;
                  delete module.media;
                  delete module.status;
                  delete module.upload_content;

                  return module;
                } else {
                  handleCollapseChange([...activeKeys, i]);
                  form.setFields([
                    {
                      name: ['modules', i, 'title'],
                      errors: [
                        'Error missing data for required field. Check if the data is valid and try again.',
                      ],
                    },
                  ]);
                  imageUploadErrors.push(i);
                }
              })
            );
          }
        } else {
          payload[input] = values[input];
        }
      }
    }

    if (imageUploadErrors.length === 0) {
      if (payload.length > 0) {
        if (screen === 'add') {
          trackerActions(
            mixpanelEventsEnum.ACTIONS_PARTNER
              .LEARNINGS_ADMIN_ADD_MODULE_FROM_MODULE_SERIE
          );
          props.create(partnershipId, moduleSerieId, payload, (success) => {
            if (success) {
              successAction();
            } else {
              notification.error(getI18n().t('feedback_form_error'));
            }
          });
        } else {
          trackerActions(
            mixpanelEventsEnum.ACTIONS_PARTNER
              .LEARNINGS_ADMIN_EDIT_MODULE_FROM_MODULE_SERIE
          );
          props.update(partnershipId, moduleSerieId, payload, (success) => {
            if (success) {
              successAction();
            } else {
              notification.error(getI18n().t('feedback_form_error'));
            }
          });
        }
      } else {
        successAction();
      }
    }
  };

  const handleFinish = async (values) => {
    Logger.log('debug', `LearningAdminModuleSerieFormStep2.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log(
      'debug',
      `LearningAdminModuleSerieFormStep2.handleFinishFailed(###)`
    );
    notification.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }

    const indexError = [
      ...new Set(errorFields.map((e) => e.name[1].toString())),
    ];

    handleCollapseChange([...activeKeys, ...indexError]);
  };

  const handleValuesChange = (changedValues, allValues) => {
    for (const key of Object.keys(changedValues)) {
      form.setFields([{ name: key, errors: [] }]);
    }
    setIsChangeValue(true);
  };

  const handleCollapseChange = (key) => {
    setActiveKeys(key);
  };

  const onClickCancel = () => {
    setDefaultPublicStatus(2);
  };

  const onClickSave = () => {
    setDefaultPublicStatus(2);
  };

  const handleRemoveModule = (event, index, remove, module, moduleValue) => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER
        .LEARNINGS_ADMIN_DELETE_MODULE_FROM_MODULE_SERIE
    );
    setActiveKeys(activeKeys.filter((k) => k !== index.toString()));
    event.stopPropagation();
    remove(module.name);
    if (moduleValue.id) {
      if (deleteModuleIds.includes(moduleValue.id) === false) {
        setDeleteModuleIds([...deleteModuleIds, moduleValue.id]);
      }
    }
  };

  return (
    <Translation>
      {(t) => (
        <div className='module-serie-form-step-2'>
          <Form
            name='learning_admin_module_serie_form_step_2'
            form={form}
            initialValues={data}
            onFinish={handleFinish}
            onFinishFailed={handleFinishFailed}
            onValuesChange={handleValuesChange}
            validateTrigger='onSubmit'
            {...layout.main}
          >
            <Title level={5}>
              {t('learning_admin_form_module_serie_title_step_2')}
            </Title>

            <Form.Item>
              <Form.List name='modules'>
                {(modulesList, { add, remove }) => {
                  const modulesValues = form.getFieldValue('modules') || [];
                  return (
                    <>
                      <Collapse
                        activeKey={activeKeys}
                        onChange={handleCollapseChange}
                        style={{
                          backgroundColor: 'white',
                          marginBottom: '20px',
                        }}
                      >
                        {modulesList.map((module, i) => {
                          const moduleValue = modulesValues[module.name] || {};

                          return (
                            <Panel
                              header={
                                <Text className='module-serie-form-step-2-panel-title-text'>
                                  {moduleValue.title ??
                                    `${t(
                                      'learning_admin_form_action_add_module'
                                    )}_${i + 1}`}
                                </Text>
                              }
                              key={i}
                              showArrow={false}
                              extra={
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {moduleValue?.id && (
                                    <PublicStatusInput
                                      name={[module.name, 'public_status']}
                                      fieldKey={[
                                        module.fieldKey,
                                        'public_status',
                                      ]}
                                      disabled={isLoading || isSubmitting}
                                    />
                                  )}

                                  {moduleValue.public_status === 3 ||
                                  moduleValue.id === null ||
                                  moduleValue.id === undefined ? (
                                    <FontAwesomeIcon
                                      style={{
                                        cursor: 'pointer',
                                        marginTop: '6px',
                                      }}
                                      onClick={(event) =>
                                        handleRemoveModule(
                                          event,
                                          i,
                                          remove,
                                          module,
                                          moduleValue
                                        )
                                      }
                                      icon={faTrashCan}
                                      className='module-action-icon-delete'
                                    />
                                  ) : (
                                    <Tooltip
                                      placement='topRight'
                                      title={t(
                                        'learning_module_series_message_info_delete'
                                      )}
                                      arrowPointAtCenter
                                    >
                                      <FontAwesomeIcon
                                        icon={faTrashCan}
                                        className='module-action-icon-delete-disabled'
                                      />
                                    </Tooltip>
                                  )}
                                </div>
                              }
                            >
                              <ModuleInputRow
                                deleteImageIds={deleteImageIds}
                                setDeleteImageIds={setDeleteImageIds}
                                getSignedUrl={getSignedUrl}
                                moduleValue={moduleValue}
                                setIsChangeValue={setIsChangeValue}
                                form={form}
                                module={module}
                                disabled={isLoading || isSubmitting}
                                index={i}
                                key={i}
                                trackerActions={trackerActions}
                              />
                            </Panel>
                          );
                        })}
                      </Collapse>

                      <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                          disabled={isLoading || isSubmitting}
                          size='large'
                          onClick={() => {
                            const nextIndex = modulesList.length;
                            add();
                            handleCollapseChange([...activeKeys, nextIndex]);
                          }}
                          block
                        >
                          <PlusOutlined />
                          {t('learning_admin_form_action_add_module')}
                        </Button>
                      </Form.Item>
                    </>
                  );
                }}
              </Form.List>
            </Form.Item>

            <LearningAdminModuleSerieFormFooter
              step={step}
              screen={screen}
              disabled={isLoading || isSubmitting}
              module={modules[step]}
              htmlType='submit'
              onClickCancel={onClickCancel}
              onClickSave={onClickSave}
            />
          </Form>
        </div>
      )}
    </Translation>
  );
};

export default withRouter(LearningAdminModuleSerieFormStep2);
